/* eslint-disable max-classes-per-file */
import whenKey from 'ally.js/src/when/key';
import { getCurrentScreenSizeInt, screenSizeLG, minMediaQueryLG } from '../../global/js/screenSize';

// Primary nav vertical selector
const PRIMARY_NAV_VERTICAL_SELECTOR = '[data-component="primary-nav-vertical"]';

/*
    Collapse function selector
*/
const PRIMARY_NAV_VERTICAL_COLLAPSE_SELECTOR = '[data-click="primaryNavVerticalCollapse"]';
// Collapsed class
const PRIMARY_NAV_VERTICAL_COLLAPSED = 'primary-nav-vertical--collapsed';
const PRIMARY_NAV_HEADER_TARGET = 'header';
const PRIMARY_NAV_HEADER_COLLAPSED = 'header--nav-collapsed';

/*
    Content page expand function selector
*/
const PRIMARY_NAV_VERTICAL_MENU_EXPAND_SELECTOR = '[data-click="primaryNavVerticalMenuExpand"]';
// Expand target
const PRIMARY_NAV_VERTICAL_MENU_EXPAND_TARGET_SELECTOR = '.primary-nav-vertical-menu__list';
// Expand class
const PRIMARY_NAV_VERTICAL_MENU_EXPANDED = 'primary-nav-vertical-menu__list--active';

/*
    Collapsed content page drop down expander
*/
const PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_SELECTOR = '[data-click="primaryNavVerticalMenuDropdown"]';
// Active target
const PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_TARGET = '.primary-nav-vertical-menu';
// Active class
const PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_ACTIVE = 'primary-nav-vertical-menu--active';

/**
 * Create a new PrimaryNavVertical
 * @param {HTMLElement} PrimaryNavVertical - The HTML element of the primary nav
 * @class
 */
class PrimaryNavVerticalImpl {
    constructor(primaryNavVertical) {
        this.primaryNavVertical = primaryNavVertical;

        this.allyHandles = {
            keyHandle: undefined,
        };

        // Attach listener for 'collapse' functionality
        document.querySelectorAll(PRIMARY_NAV_VERTICAL_COLLAPSE_SELECTOR).forEach((collapser) => {
            collapser.addEventListener('click', () => {
                this.handleNavCollapse();
            });
        });

        // Attach listener for menu 'expand' functionality
        document.querySelectorAll(PRIMARY_NAV_VERTICAL_MENU_EXPAND_SELECTOR).forEach((expander) => {
            expander.addEventListener('click', () => {
                this.handleMenuExpand(expander);
            });
        });

        // Attach listener for 'dropdown' functionality
        document.querySelectorAll(PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_SELECTOR).forEach((dropdown) => {
            dropdown.addEventListener('click', () => {
                this.handleDropdownOpen();
            });
        });

        // Save this for later usage, needed as bind creates a new function
        this.dropdownFocusOutHandler = this.handleDropdownFocusOut.bind(this);

        this.watchForAutoCollapseExpand();
        if (getCurrentScreenSizeInt() <= screenSizeLG) {
            this.handleNavCollapse({ forceCollapse: true });
        }
    }

    handleNavCollapse({ forceCollapse, forceExpand } = {}) {
        // Get the current collapsed state
        let isCollapsed = this.primaryNavVertical.classList.contains(PRIMARY_NAV_VERTICAL_COLLAPSED);

        isCollapsed = forceCollapse === true ? false : isCollapsed;
        isCollapsed = forceExpand === true ? true : isCollapsed;

        const header = document.querySelector(PRIMARY_NAV_HEADER_TARGET);

        // Toggle the collapsed state
        if (isCollapsed) {
            this.primaryNavVertical.classList.remove(PRIMARY_NAV_VERTICAL_COLLAPSED);

            header?.classList.remove(PRIMARY_NAV_HEADER_COLLAPSED);
        } else {
            this.primaryNavVertical.classList.add(PRIMARY_NAV_VERTICAL_COLLAPSED);

            header?.classList.add(PRIMARY_NAV_HEADER_COLLAPSED);
        }

        document.querySelectorAll(PRIMARY_NAV_VERTICAL_COLLAPSE_SELECTOR).forEach((collapser) => {
            // isCollapsed is used even after the change above as aria-expanded a reverse term
            collapser.setAttribute('aria-expanded', isCollapsed);
        });
    }

    handleMenuExpand(expanderElement) {
        // Find the menu to expand
        const menuTarget = expanderElement.parentElement.querySelector(PRIMARY_NAV_VERTICAL_MENU_EXPAND_TARGET_SELECTOR);

        // Get the current expantion state
        const isExpanded = menuTarget.classList.contains(PRIMARY_NAV_VERTICAL_MENU_EXPANDED);

        // Toggle the expantion based on current state
        if (isExpanded) {
            menuTarget.classList.remove(PRIMARY_NAV_VERTICAL_MENU_EXPANDED);
        } else {
            menuTarget.classList.add(PRIMARY_NAV_VERTICAL_MENU_EXPANDED);
        }

        // Update aria property
        expanderElement.setAttribute('aria-expanded', !isExpanded);
    }

    handleDropdownOpen() {
        // Find the menu to expand
        const menu = this.primaryNavVertical.querySelector(PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_TARGET);

        // Get the current active state
        const isActive = menu.classList.contains(PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_ACTIVE);

        // Toggle the active based on current state
        if (isActive) {
            this.closeDropdown();
        } else {
            menu.classList.add(PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_ACTIVE);

            // Close drop down on ESC (accessibility)
            this.allyHandles.keyHandle = whenKey({
                escape: () => {
                    setTimeout(() => {
                        this.closeDropdown();
                    });
                },
            });

            // Listen for the focus to leave the menu area
            menu.addEventListener('focusout', this.dropdownFocusOutHandler);
        }

        // Update aria property
        document.querySelectorAll(PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_SELECTOR).forEach((opener) => {
            opener.setAttribute('aria-expanded', !isActive);
        });
    }

    handleDropdownFocusOut(e) {
        // Check if the menu area still contains focus inside it
        if (!e.currentTarget.contains(e.relatedTarget)) {
            this.closeDropdown();
        }
    }

    closeDropdown() {
        // Find the menu to expand
        const menu = this.primaryNavVertical.querySelector(PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_TARGET);
        menu.classList.remove(PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_ACTIVE);

        // Update aria property
        document.querySelectorAll(PRIMARY_NAV_VERTICAL_MENU_DROPDOWN_SELECTOR).forEach((opener) => {
            opener.setAttribute('aria-expanded', false);
        });

        // Disable ESC listener
        if (this.allyHandles.keyHandle) {
            this.allyHandles.keyHandle.disengage();
        }

        // Remove focusout listener
        menu.removeEventListener('focusout', this.dropdownFocusOutHandler);
    }

    watchForAutoCollapseExpand() {
        this.mediaWatch = window.matchMedia(`(${minMediaQueryLG})`);

        // Have to save the handler function so it can be removed later
        this.mediaWatchChangeHandler = (e) => {
            if (e.matches) {
                this.handleNavCollapse({ forceExpand: true });
            } else {
                this.handleNavCollapse({ forceCollapse: true });
            }
        };

        // Watch for a change and let change handler callback handle it
        this.mediaWatch.addEventListener('change', this.mediaWatchChangeHandler);
    }
}

/**
 * Singleton wrapper for Primary Nav Vertical to allow control from other areas of the template.
 * @class
 */
export default class PrimaryNavVertical {
    static getInstance() {
        const primaryNavVertical = document.querySelector(PRIMARY_NAV_VERTICAL_SELECTOR);

        // doesnt exist, return null
        if (!primaryNavVertical) {
            return null;
        }

        // Doesnt exist yet, create
        if (!primaryNavVertical._primaryNavVertical) {
            // Store the against the DOM, as if this is destroyed for any reason it will need to be re-instantiated
            primaryNavVertical._primaryNavVertical = new PrimaryNavVerticalImpl(primaryNavVertical);
        }

        // Return object
        return primaryNavVertical._primaryNavVertical;
    }
}

// Export for external access and run intial pass
PrimaryNavVertical.getInstance();
